/***
 *
 *   API Keys List
 *   View and manage the API keys in the account.
 *
 **********/

import { Fragment, useContext, useState, useEffect, useCallback } from "react";
import Axios from "axios";
import {
  ViewContext,
  Animate,
  Card,
  Table,
  Loader,
  Alert,
  useAPI,
  useNavigate,
  useToast,
} from "components/lib";

export function APIKeyList({ t }) {
      const userPermission_local=JSON.parse(localStorage.getItem('user'))?.permission;
  const navigateTo = useNavigate();
  const { toast } = useToast();
  // context
  const viewContext = useContext(ViewContext);

  // Prevent horizontal scroll on mount
  useEffect(() => {
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';
    return () => {
      document.body.style.overflowX = 'auto';
      document.documentElement.style.overflowX = 'auto';
    };
  }, []);

  // state
  const [keys, setKeys] = useState([]);

  // fetch
  const fetch = useAPI("/api/key");

  // init keys
  useEffect(() => {
    if (fetch?.data?.length) setKeys(fetch.data);
  }, [fetch]);

  const newKey = useCallback(() => {
    // navigate() doesn't work inside a shadcn dropdown
    // so using window.location
    navigateTo("/keys/create");
    // window.location = "/keys/create";
  }, []);

  const revokeKey = useCallback(
    ({ row, editRowCallback }) => {
      viewContext.dialog.open(
        {
          title: t("api_keys.list.revoke.title"),
          description: t("api_keys.list.revoke.description"),
          form: {
            inputs: {
              active: {
                type: "hidden",
                value: false,
              },
            },
            buttonText: t("api_keys.list.revoke.button"),
            url: `/api/key/${row.id}`,
            destructive: true,
            method: "PATCH",
          },
        },
        () => {
          row.active = false;
          editRowCallback(row);
        }
      );
    },
    [viewContext]
  );

  const editKey = useCallback(({ row }) => {
    // navigate() doesn't work inside a shadcn dropdown
    // so using window.location
    navigateTo(`/keys/edit?id=${row.id}`);
  }, []);

  const deleteKey = useCallback(
    ({ row, deleteRowCallback }) => {
      const id = Array.isArray(row) ? row.map((x) => x.id) : [row.id];

      viewContext.dialog.open(
        {
          title:
            id.length > 1
              ? t("api_keys.list.delete.title.plural")
              : t("api_keys.list.delete.title.single"),
          description:
            id.length > 1
              ? t("api_keys.list.delete.description.plural")
              : t("api_keys.list.delete.description.single"),
          form: {
            inputs: {
              id: {
                type: "hidden",
                value: id,
              },
            },
            url: "/api/key",
            destructive: true,
            method: "DELETE",
            buttonText: t("api_keys.list.delete.button"),
          },
        },
        () => {
          const newState = deleteRowCallback(row);
          setKeys(newState);
        }
      );
    },
    [viewContext]
  );

  const revealKey = useCallback(
    async ({ row }) => {
      // reveal the api key
      const res = await Axios.get(`/api/key/${row.id}`);
      const key = res.data?.data?.[0].key;

      setKeys((prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, key } : item))
      );
    },
    [keys]
  );

  const copyKey = useCallback(
    async ({ row }) => {
      try {
        // Always fetch the full key from the API to avoid copying truncated version
        const res = await Axios.get(`/api/key/${row.id}`);
        const fullKey = res.data?.data?.[0].key;

        // Copy the full key to clipboard
        await navigator.clipboard.writeText(fullKey);

        // Show success toast
        toast({
          title: "Key copied",
          description: "API key has been copied to your clipboard"
        });
      } catch (error) {
        console.error("Failed to copy API key:", error);

        // Show error toast
        toast({
          title: "Copy failed",
          description: "Failed to copy API key. Please try again.",
          variant: "destructive"
        });
      }
    },
    [toast]
  );
  // console.log(keys, "keys");
  return (
    <div className="min-h-screen w-full overflow-x-hidden" style={{ maxWidth: '100vw' }}>
      <div
        className="py-3 w-full box-border px-6 overflow-x-hidden"
        style={{
          width: window.innerWidth < 640 ? 'calc(100vw - 24px)' : 'calc(100vw - 256px - 24px)',
          maxWidth: window.innerWidth < 640 ? 'calc(100vw - 24px)' : 'calc(100vw - 256px - 24px)'
        }}
      >
        <Fragment>
          {fetch.loading ? (
            <Loader />
          ) : (
            <Animate>
          <Card title={t("api_keys.list.label")}>
            {keys.length ? (
              <Table
                searchable
                data={keys}
                loading={fetch.loading}
                translation="api_keys.list"
                header={["name", "key", "allowed_websites", "active"]}
                show={["name", "key", "allowed_websites", "active"]}
                badge={{
                  col: "active",
                  color: "green",
                  condition: [
                    { value: true, color: "green" },
                    { value: false, color: "red" },
                  ],
                }}
                actions={[
                  {
                    icon: "eye",
                    label: t("global.table.action.reveal"),
                    action: revealKey,
                  },
                  {
                    icon: "copy",
                    label: "Copy",
                    action: copyKey,
                  },
                 userPermission_local!=='user'&&  {
                    icon: "trash",
                    label: t("global.table.action.delete"),
                    action: deleteKey,
                    color: "red",
                  },
                 userPermission_local!=='user'&& {
                    icon: "circle-plus",
                    label: t("global.table.action.new"),
                    action: newKey,
                    color: "green",
                    global: true,
                    globalOnly: true,
                  },
                ]}
              />
            ) : (
              <Alert
                variant="info"
                title={t("api_keys.list.blank_slate.title")}
                description={t("api_keys.list.blank_slate.description")}
                button={{
                  text: t("api_keys.list.blank_slate.button"),
                  url: "/keys/create",
                }}
              />
            )}
          </Card>
        </Animate>
      )}
    </Fragment>
      </div>
    </div>
  );
}
